import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import RouteService, { RouteType } from "@/src/application/services/route-service";
import { useInject } from "@/src/presentation/hooks/useInject";
import { useBrand } from "@/src/brand";
import WaterbureauHeader from "@/src/brand/header/waterbureau-header";
import SimpleHeader from "@/src/brand/header/simple-header";
import ViewmodelWrapper from "@/src/presentation/components/viewmodel-wrapper";
import DevicesViewModel from "@/src/presentation/view-models/devices-viewmodel";
import { InjectedDeviceAddDevicesButton } from "./dashboard/devices/_components/device-controls";

export function isDevicesIndexPath(pathname: string) {
  return pathname.replace(/\/+$/, "") === "/dashboard/devices";
}

export function shouldShowDeviceHeaderAction(
  pathname: string,
  addDeviceButtonPlacement: "top" | "bottom" | "header",
) {
  return addDeviceButtonPlacement === "header" && isDevicesIndexPath(pathname);
}

const AuthenticatedLayout = observer(function AuthenticatedLayout() {
  const authService = useInject<AuthService>(AuthService);
  const routeService = useInject<RouteService>(RouteService);
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const showDeviceHeaderAction = shouldShowDeviceHeaderAction(
    location.pathname,
    brand.features.addDeviceButtonPlacement,
  );

  // Set route type
  routeService.setRouteType(RouteType.PRIVATE);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (authService.status === AuthenticationStatus.NotAuthenticated) {
      navigate({ to: "/login" });
    }
  }, [authService.status, navigate]);

  // Show loading or nothing while redirecting
  if (authService.status !== AuthenticationStatus.Authenticated) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="size-16 animate-spin rounded-full border-8 border-dashed border-brand-accent" />
      </div>
    );
  }

  const content = (
    <div className="flex min-h-screen flex-col">
        {brand.header.variant === "waterbureau" ? (
          <WaterbureauHeader
            headerAction={showDeviceHeaderAction ? <InjectedDeviceAddDevicesButton /> : undefined}
          />
        ) : (
          <SimpleHeader
            headerAction={showDeviceHeaderAction ? <InjectedDeviceAddDevicesButton /> : undefined}
          />
        )}
      <main className="flex-1 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>
      <footer className="border-t bg-white py-4">
        <div className="text-muted-foreground container mx-auto px-4 text-center text-sm">
          {brand.strings.copyright}
        </div>
      </footer>
    </div>
  );

  if (showDeviceHeaderAction) {
    return (
      <ViewmodelWrapper global={true} type={DevicesViewModel}>
        {content}
      </ViewmodelWrapper>
    );
  }

  return content;
});

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});
